// Cost-centre transfers (#514) — the pure half of the cooperator screens.
//
// The API is the gate for everything here: these helpers only decide what to
// show. A button hidden by mistake is a missing button; a button shown by
// mistake is answered 403/404 by the server.

// transferMode mirrors costCenterTransfers.EffectiveMode on the server:
// anything it does not recognise, including a missing setting, is off — and so
// is any value at all in an organisation without a transactions ledger, where
// the API answers 404 to every transfer route (#514 review).
export function transferMode(settingsByName) {
  if (settingsByName?.hasTransactionsLedger?.value === 'false') return 'off'
  const v = (settingsByName?.costCenterTransferMode?.value ?? '').trim()
  return v === 'approval' || v === 'automatic' ? v : 'off'
}

// canSend — the «Enviar» action on a cost centre: the feature is on and the
// user holds «Pode Fazer Pedidos» on that cost centre.
export function canSend(mode, canMakeRequests) {
  return mode !== 'off' && canMakeRequests === true
}

export const transferStatusLabels = {
  submitted: 'A aguardar aprovação',
  approved: 'Concluída',
  rejected: 'Recusada',
  cancelled: 'Cancelada',
}

export function transferStatusLabel(status) {
  return transferStatusLabels[status] ?? status ?? ''
}

// fieldErrorMessages turns the API's 422 markers into the sentence each field
// shows. Unknown markers fall back to a generic sentence rather than showing
// the raw marker. context.available, when known, is quoted in the balance
// refusal so the sender sees how much they can send.
const messages = {
  amount: {
    insufficientBalance: 'O saldo disponível não chega para este valor.',
    'gt=0': 'Indique um valor positivo, com até duas casas decimais.',
  },
  toCostCenterId: {
    required: 'Escolha o centro de custo de destino.',
    invalid: 'Escolha o centro de custo de destino.',
    'nefield=FromCostCenterID': 'Escolha um centro de custo diferente do de origem.',
  },
  fromCostCenterId: {
    required: 'Centro de custo de origem em falta.',
  },
  reviewNotes: {
    required: 'Indique o motivo.',
  },
}

export function fieldErrorMessages(errors, context = {}) {
  const out = {}
  for (const [field, marker] of Object.entries(errors ?? {})) {
    if (field === 'amount' && marker === 'insufficientBalance' && context.available != null) {
      out[field] = `O saldo disponível (${context.available} €) não chega para este valor.`
      continue
    }
    out[field] = messages[field]?.[marker] ?? 'Valor inválido.'
  }
  return out
}

// sentMessage is what the sender is told once the API accepted the transfer.
export function sentMessage(transfer) {
  if (transfer?.status === 'approved') {
    return `Transferência de ${transfer.amount} € para ${transfer._toCostCenterName} feita.`
  }
  return `Transferência de ${transfer?.amount} € para ${transfer?._toCostCenterName} enviada para aprovação da tesouraria. O valor fica reservado até lá.`
}

// canCancel — only the sender, only while it is still waiting.
export function canCancel(transfer, userId) {
  return transfer?.status === 'submitted' && !!userId && transfer?.submittedByUserId === userId
}
